import React from 'react';

import { DialogContentText } from '@mui/material';

import { makeButtonCancelTestId, makeButtonConfirmTestId } from 'app/utils';

import { TranslationKeys } from '../../translations';

import ActionButton from '../ActionButton';
import { Checkbox } from '../Checkbox';
import { Dialog } from '../Dialog';

import { BoxCheckboxContainerStyled } from './ConfirmDialogWithCheckbox.styles';

type ConfirmDialogWithCheckboxProps = {
  children: React.ReactNode;
  title: string | React.ReactElement;
  checkboxLabel: string | React.ReactElement;
  open: boolean;
  onClose?: () => void;
  setOpen?: (state: boolean) => void;
  onConfirm?: () => void;
  testId?: string;
  cancelButtonMessageId?: string;
  confirmButtonMessageId?: string;
};

export const ConfirmDialogWithCheckbox = ({
  title,
  children,
  checkboxLabel,
  open,
  setOpen,
  onConfirm,
  onClose,
  testId,
  cancelButtonMessageId = TranslationKeys.no,
  confirmButtonMessageId = TranslationKeys.yes,
}: ConfirmDialogWithCheckboxProps) => {
  const [checked, setChecked] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setChecked(false);
    }
  }, [open]);

  const closeDialog = React.useCallback(() => {
    if (onClose) {
      onClose();
    }
    if (setOpen) {
      setOpen(false);
    }
  }, [onClose, setOpen]);

  const onCheckboxChange = React.useCallback((_event, value: boolean) => {
    setChecked(value);
  }, []);

  const onConfirmButtonClick = React.useCallback(() => {
    setLoading(true);
    onConfirm && onConfirm();
    closeDialog();
    setLoading(false);
  }, [closeDialog, onConfirm]);

  const DialogActions = React.useMemo(() => {
    return (
      <>
        <ActionButton
          messageId={cancelButtonMessageId}
          onClick={closeDialog}
          variant="outlined"
          testId={makeButtonCancelTestId(testId)}
        />
        <ActionButton
          messageId={confirmButtonMessageId}
          onClick={onConfirmButtonClick}
          disabled={!checked || loading}
          testId={makeButtonConfirmTestId(testId)}
        />
      </>
    );
  }, [cancelButtonMessageId, checked, closeDialog, confirmButtonMessageId, loading, onConfirmButtonClick, testId]);

  return (
    <Dialog open={open} title={title} onClose={closeDialog} actions={DialogActions} testId={testId}>
      <DialogContentText component={'div'}>{children}</DialogContentText>
      <BoxCheckboxContainerStyled>
        <Checkbox
          name={'confirmDialogWithCheckbox'}
          checked={checked}
          onChange={onCheckboxChange}
          label={checkboxLabel}
          testId={testId ? `${testId}-checkbox` : undefined}
        />
      </BoxCheckboxContainerStyled>
    </Dialog>
  );
};
